/**
 * Loads the payment provider's checkout script, once, on demand.
 *
 * This is the only third-party script in the application, and it is deliberately kept off the
 * critical path: it loads when somebody opens a billing panel, not when they open their drawer.
 * Nobody who never subscribes ever fetches it.
 */

interface PaddleCheckoutOptions {
  transactionId: string;
  customer?: { email: string };
  settings?: { theme?: 'light' | 'dark' };
}

interface PaddleEvent {
  name: string;
}

interface Paddle {
  Environment: { set(environment: string): void };
  Initialize(options: { token: string; eventCallback?: (event: PaddleEvent) => void }): void;
  Checkout: { open(options: PaddleCheckoutOptions): void };
}

/** How a checkout ended. */
export type CheckoutOutcome = 'completed' | 'closed';

/**
 * Resolves the checkout currently on screen.
 *
 * The overlay is fire-and-forget — `Checkout.open` returns the instant it is asked to display,
 * not when anybody has paid. Without listening for the events, everything downstream ran while
 * the card form was still empty, and the panel refreshed long before there was anything new to
 * see.
 */
let settleCheckout: ((outcome: CheckoutOutcome) => void) | null = null;

function handleEvent(event: PaddleEvent): void {
  if (event.name !== 'checkout.completed' && event.name !== 'checkout.closed') {
    return;
  }

  const settle = settleCheckout;
  settleCheckout = null;

  settle?.(event.name === 'checkout.completed' ? 'completed' : 'closed');
}

declare global {
  interface Window {
    Paddle?: Paddle;
  }
}

const scriptUrl = 'https://cdn.paddle.com/paddle/v2/paddle.js';

let loading: Promise<Paddle> | null = null;

function token(): string {
  return import.meta.env.VITE_PADDLE_CLIENT_TOKEN ?? '';
}

/** True when this build has been given the credentials to open a checkout at all. */
export function isCheckoutConfigured(): boolean {
  return token().length > 0;
}

async function load(): Promise<Paddle> {
  if (window.Paddle) {
    return window.Paddle;
  }

  // Memoised, so two panels mounting at once do not race two <script> tags into the document.
  loading ??= new Promise<Paddle>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = scriptUrl;
    script.async = true;

    script.onload = () => {
      const paddle = window.Paddle;
      if (!paddle) {
        reject(new Error('The payment script loaded but did not register itself.'));
        return;
      }

      const environment = import.meta.env.VITE_PADDLE_ENV ?? 'sandbox';
      if (environment !== 'production') {
        paddle.Environment.set(environment);
      }

      paddle.Initialize({ token: token(), eventCallback: handleEvent });
      resolve(paddle);
    };

    script.onerror = () => {
      // Let the next attempt try again rather than caching the failure forever.
      loading = null;
      reject(new Error('The payment script could not be loaded.'));
    };

    document.head.append(script);
  });

  return loading;
}

/**
 * Opens the checkout overlay for a transaction the API created.
 *
 * The email is only a prefill. The transaction already carries which team is being paid for, set
 * server-side, so nothing here decides anything that costs money.
 */
export async function openCheckout(
  transactionId: string,
  email?: string,
): Promise<CheckoutOutcome> {
  if (!isCheckoutConfigured()) {
    throw new Error('Checkout is not configured on this deployment.');
  }

  const paddle = await load();

  const finished = new Promise<CheckoutOutcome>((resolve) => {
    // A checkout already on screen cannot be paid for any more; let its waiter go rather than
    // stranding it forever.
    settleCheckout?.('closed');
    settleCheckout = resolve;
  });

  paddle.Checkout.open({
    transactionId,
    ...(email ? { customer: { email } } : {}),
  });

  return finished;
}
